import { Box, Grid, Stack, styled, SvgIcon, Theme, Typography, useMediaQuery } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppConfig,
  EconsultUserRoles,
  NameUtils,
  NoteActionTypes,
  dateUtils,
  storageUtils,
  EconsultUtils
} from '@oh-vcp/components-common';

import {
  DynamicLink,
  EmrIcon,
  PDFIconWhite,
  RedirectService,
  useNotifierContext
} from '@oh-vcp/components-ui';
import moment from 'moment';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import EconsultPatientDetailsContainer from '../econsultPatientDetailsContainer/EconsultPatientDetailsContainer';
import EconsultExportEmrDialog from '../econsultExportEmrDialog/EconsultExportEmrDialog';
import CaseService from '../services/caseService';
import { NotificationFactory } from '../notifier/NotificationFactory';
import EconsultCaseFlagSelector from '../econsultCaseFlagSelector/EconsultCaseFlagSelector';
import EconsultRequesterDetails from '../econsultRequesterDetails/EconsultRequesterDetails';
import EconsultSpecialistDetails from '../econsultSpecialistDetails/EconsultSpecialistDetails';
import useAuditMutation from '../services/useAuditMutation';

const LinkButton = styled(Typography)({
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
    cursor: 'pointer'
  }
});

const IconContainer = styled(Box)<{ isActive: boolean }>(({ theme, isActive }) => ({
  backgroundColor: isActive ? theme.palette.primary.main : theme.palette.base.grey5,
  height: 35,
  width: 35,
  borderRadius: '50%',
  paddingLeft: 3,
  paddingTop: 3,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: isActive ? theme.palette.primary.custom3 : theme.palette.base.grey5
  }
}));

const EmrContainer = styled(Box)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.base.grey5,
  paddingTop: '20px !important',
  padding: 5,
  paddingLeft: 10,
  width: '75%',
  marginBottom: 10,
  marginTop: -5
}));

interface EconsultCaseHeaderProps {
  isPdfEnabled: boolean;
  isEmrEnabled: boolean;
}

const EconsultCaseHeader: FC<EconsultCaseHeaderProps> = ({ isPdfEnabled, isEmrEnabled }) => {
  const { t } = useTranslation();
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const { caseDetails, userRole, userRoleDual } = useEconsultCaseDetailsContext();
  const [emrButtonDisabled, setEmrButtonDisabled] = useState<boolean>(false);
  const [isEmrDialogOpen, setIsEmrDialogOpen] = useState<boolean>(false);
  const [isPatientDetailsShown, setIsPatientDetailsShown] = useState<boolean>(false);
  const [isRequesterDetailsShown, setIsRequesterDetailsShown] = useState<boolean>(false);
  const [isSpecialistDetailsShown, setIsSpecialistDetailsShown] = useState<boolean>(false);
  const [isFlagShown, setIsFlagShown] = useState<boolean>(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [isEmrShown, setIsEmrShown] = useState<boolean>(false);
  const { mutateAsync: addAuditAsync } = useAuditMutation();
  const curUserRole = userRoleDual || userRole;
  useEffect(() => {
    if (
      ((curUserRole === EconsultUserRoles.consultant ||
        curUserRole === EconsultUserRoles.consultantDelegate) &&
        caseDetails?.participant?.lastHrmOn) ||
      ((curUserRole === EconsultUserRoles.referrer ||
        curUserRole === EconsultUserRoles.referrerDelegate) &&
        caseDetails?.lastHrmOn)
    ) {
      setEmrButtonDisabled(true);
    } else {
      setEmrButtonDisabled(false);
    }
    if (
      curUserRole === EconsultUserRoles.consultant ||
      curUserRole === EconsultUserRoles.consultantDelegate
    ) {
      setIsFlagShown(true);
    } else {
      setIsFlagShown(false);
    }
  }, [caseDetails, curUserRole]);

  if (!caseDetails) {
    return null;
  }

  const { referrer, participant, patient, caseId } = caseDetails;
  const getPatientGender = () => {
    const genderName = NameUtils.makeGenderName(patient?.gender ?? undefined);
    if (genderName) {
      return `, ${genderName}`;
    }
    return '';
  };
  const getPatientAge = () => {
    if (!patient?.dateOfBirth) {
      return '';
    }
    const age = dateUtils.getAge(new Date(patient.dateOfBirth));
    if (age) {
      return `, ${age} ${t('Case.detials.yearsOld')}`;
    }
    return '';
  };
  const exportToPdf = () => {
    const token = storageUtils.getUserToken();
    const url = `${AppConfig.apiHost}cases/export/${caseId}?api_access_token=${token}`;
    window.location.href = url;
  };
  const exportToEmr = async () => {
    setIsEmrDialogOpen(false);
    if (caseId) {
      removeAllErrorNotifications();
      const response = await CaseService.exportToHrm(caseId.toString());
      if (
        response &&
        response['Mirth-Response'] &&
        response['Mirth-Response'].indexOf('|AA|') > -1
      ) {
        addNotification(
          NotificationFactory.createNotification('success', {
            message: `${t('Case.emr.caseId')} ${caseId} - ${t('Case.emr.exportSuccess')}`
          })
        );
      } else {
        addNotification(
          NotificationFactory.createNotification('error', {
            message: `${t('Case.emr.caseId')} - ${t('Case.emr.exportFailed')}`
          })
        );
      }
    }
  };

  const specialistHasProfile = () => {
    return participant?.consultant?.contactId && participant?.consultant?.publishedProfile === 1;
  };

  const hideSpecialist = () => {
    const lastActiveNote = CaseService.getLastActiveNote(caseDetails);
    return (
      !caseDetails?.participant?.consultant ||
      (lastActiveNote && lastActiveNote.actionTypeCd === NoteActionTypes.unassign)
    );
  };
  const cols = (isPdfEnabled ? 3 : 0) + (isEmrEnabled ? 3 : 0) + (isFlagShown ? 4 : 0);
  const isTab = window.innerWidth < 900;

  const getSentStatusMessage = () => {
    if (
      (curUserRole === EconsultUserRoles.consultant ||
        curUserRole === EconsultUserRoles.consultantDelegate) &&
      caseDetails?.participant?.lastHrmOn
    ) {
      return moment(caseDetails.participant.lastHrmOn).format('MMM DD, yyyy h:mm A');
    }
    if (
      (curUserRole === EconsultUserRoles.referrer ||
        curUserRole === EconsultUserRoles.referrerDelegate) &&
      caseDetails?.lastHrmOn
    ) {
      return moment(caseDetails.lastHrmOn).format('MMM DD, yyyy h:mm A');
    }
    return '';
  };

  const handlePatientDetailsToggle = async () => {
    if (!isPatientDetailsShown && caseId) {
      await addAuditAsync({
        actionType: 'VIEW_PATIENT',
        domainId: caseId as number,
        domainType: 'CASES'
      });
    }
    setIsPatientDetailsShown(!isPatientDetailsShown);
  };

  const handleReferrerClicked = () => {
    if (referrer?.contactId && referrer.contactId >= 0) {
      RedirectService.redirectToDirectoryPeopleProfile(referrer.contactId.toString());
    } else {
      setIsRequesterDetailsShown(!isRequesterDetailsShown);
    }
  };

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={12} md={2}>
          <Typography variant="body2" color="base.grey4" mb={isMobile ? 0 : 2}>
            {t('Case.detials.requester')}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <LinkButton onClick={handleReferrerClicked}>
            <Typography variant="body2" color="primary.main" mb={2}>
              {NameUtils.makeName(
                referrer?.lastName || '',
                referrer?.firstName || '',
                referrer?.salutation || '',
                null,
                null,
                null
              )}
            </Typography>
          </LinkButton>
        </Grid>
        {(EconsultUtils.isMobileDevice() || isTab) && cols > 0 && (
          <Grid item xs={12 - cols} md={1} />
        )}
        <Grid item xs={cols} md={4} justifyContent={isMobile ? '' : 'flex-end'}>
          <Stack
            direction="row"
            columnGap={1}
            justifyContent={isMobile ? '' : 'flex-end'}
            sx={{ mb: isMobile ? 2 : 0 }}>
            {isFlagShown ? <EconsultCaseFlagSelector /> : <Box sx={{ width: 150 }} />}
            {isPdfEnabled && (
              <IconContainer
                title="Export to PDF"
                onClick={exportToPdf}
                isActive
                sx={{ padding: 0 }}>
                <img src={PDFIconWhite} alt="Export to PDF" />
              </IconContainer>
            )}
            {isEmrEnabled && (
              <IconContainer
                title={
                  getSentStatusMessage()
                    ? `Export to EMR is enabled\nLast updated on ${getSentStatusMessage()}`
                    : 'Export case to EMR'
                }
                onClick={() => setIsEmrDialogOpen(true)}
                isActive={!emrButtonDisabled}>
                <SvgIcon component={EmrIcon} inheritViewBox />
              </IconContainer>
            )}
          </Stack>
        </Grid>
        {isRequesterDetailsShown && referrer && (
          <>
            <Grid item xs={12} md={2} />
            <Grid item xs={12} md={10}>
              <Box mb={2}>
                <EconsultRequesterDetails referrer={referrer} />
              </Box>
            </Grid>
          </>
        )}
        {caseDetails.actionSourceCd && (
          <>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="base.grey4" mb={2}>
                {t('Case.detials.emr')}
              </Typography>
            </Grid>
            {(!caseDetails.emrTags || caseDetails.emrTags.length === 0) && (
              <Grid item xs={12} md={10}>
                <Typography variant="body2" color="base.grey3" mb={2}>
                  {caseDetails.actionSourceCd}
                </Typography>
              </Grid>
            )}
            {caseDetails.emrTags && caseDetails.emrTags.length > 0 && (
              <Grid item xs={12} md={10}>
                <LinkButton onClick={() => setIsEmrShown(!isEmrShown)}>
                  <Typography variant="body2" color="primary.main" mb={2}>
                    {caseDetails.actionSourceCd}
                  </Typography>
                </LinkButton>
              </Grid>
            )}
          </>
        )}
        {caseDetails.actionSourceCd &&
          isEmrShown &&
          caseDetails.emrTags &&
          caseDetails.emrTags.length > 0 && (
            <>
              <Grid item xs={12} md={2}>
                <Typography mb={2} />
              </Grid>
              <Grid item xs={12} md={10}>
                <EmrContainer>
                  {caseDetails.emrTags
                    .filter((ta) => ta.name !== 'null')
                    .map((ta: any) => {
                      return (
                        <Typography variant="body2" color="base.grey3" mb={2}>
                          {ta.name}: {ta.value}
                        </Typography>
                      );
                    })}
                </EmrContainer>
              </Grid>
            </>
          )}
        {participant?.program && (
          <>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="base.grey4" mb={isMobile ? 0 : 2}>
                {t('Case.detials.group')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={10}>
              <DynamicLink
                href={`${AppConfig.baseHubUrl}/#/programs/${participant?.program?.programId}`}>
                <Typography variant="body2" color="primary.main" mb={2}>
                  {participant?.program?.programName}
                </Typography>
              </DynamicLink>
            </Grid>
          </>
        )}
        {participant?.consultant && !hideSpecialist() && (
          <>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="base.grey4" mb={isMobile ? 0 : 2}>
                {t('Case.detials.specialist')}
              </Typography>
            </Grid>
            {specialistHasProfile() && (
              <Grid item xs={8} md={4}>
                <DynamicLink
                  target="_blank"
                  href={`${AppConfig.baseHubUrl}/#/profile/people/${participant?.consultant?.contactId}`}>
                  <Typography variant="body2" color="primary.main" mb={2}>
                    {NameUtils.makeName(
                      participant?.consultant?.lastName || '',
                      participant?.consultant?.firstName,
                      participant?.consultant?.salutation,
                      null,
                      null,
                      null
                    )}
                  </Typography>
                </DynamicLink>
              </Grid>
            )}
            {!specialistHasProfile() && (
              <Grid item xs={8} md={10}>
                <LinkButton onClick={() => setIsSpecialistDetailsShown(!isSpecialistDetailsShown)}>
                  <Typography variant="body2" color="primary.main" mb={2}>
                    {NameUtils.makeName(
                      participant?.consultant?.lastName || '',
                      participant?.consultant?.firstName,
                      participant?.consultant?.salutation,
                      null,
                      null,
                      null
                    )}
                  </Typography>
                </LinkButton>
              </Grid>
            )}
            {isSpecialistDetailsShown && participant?.consultant && (
              <>
                <Grid item xs={12} md={2} />
                <Grid item xs={12} md={10}>
                  <Box mb={2}>
                    <EconsultSpecialistDetails specialist={participant?.consultant} />
                  </Box>
                </Grid>
                <Grid xs={4} md={6} />
              </>
            )}
            <Grid xs={4} md={12} />
          </>
        )}
        <Grid item xs={12} md={2}>
          <Typography variant="body2" color="base.grey4" mb={isMobile ? 0 : 2}>
            {t('Case.detials.patient')}
          </Typography>
        </Grid>
        <Grid item xs={12} md={10}>
          <Stack direction="row" flexWrap="wrap">
            <LinkButton
              variant="body2"
              color="primary.main"
              mb={0}
              onClick={handlePatientDetailsToggle}>
              {patient?.firstName === '***'
                ? '*****'
                : NameUtils.makeName(
                    patient?.lastName || '',
                    patient?.firstName || '',
                    null,
                    null,
                    patient?.middleName || '',
                    null,
                    null,
                    null,
                    null
                  )}
            </LinkButton>
            {patient?.gender !== '***' && (
              <Typography variant="body2">{getPatientGender()}</Typography>
            )}
            <Typography variant="body2" mr={1}>
              {getPatientAge()}
            </Typography>
            {((caseDetails?.patient?.noOhip && caseDetails?.patient.ohipNumber !== '***') ||
              !caseDetails?.patient?.ohipNumber) &&
              !isMobile && (
                <Typography color="error" variant="body3">
                  ({t('Case.consult.ohipNotAvailable')})
                </Typography>
              )}
            {caseDetails?.patientConsent && caseDetails?.patientConsent !== '***' && !isMobile && (
              <Typography color="error" ml={1} variant="body3">
                ({t('Case.consult.consentDirectives')})
              </Typography>
            )}
          </Stack>
          {caseDetails?.patientConsent && caseDetails?.patientConsent !== '***' && isMobile && (
            <Stack>
              <Typography color="error" ml={1} variant="body3">
                ({t('Case.consult.consentDirectives')})
              </Typography>
            </Stack>
          )}
          {((caseDetails?.patient?.noOhip && caseDetails?.patient.ohipNumber !== '***') ||
            !caseDetails?.patient?.ohipNumber) &&
            isMobile && (
              <Stack mb={2}>
                <Typography color="error" ml={1} variant="body3">
                  ({t('Case.consult.ohipNotAvailable')})
                </Typography>
              </Stack>
            )}
        </Grid>
        {isPatientDetailsShown && (
          <>
            <Grid item xs={12} md={2} />
            <Grid item xs={12} md={10}>
              <Box mb={2}>
                <EconsultPatientDetailsContainer />
              </Box>
            </Grid>
          </>
        )}
        <Grid item xs={12} md={2}>
          <Typography variant="body2" color="base.grey4" mb={2}>
            {t('Case.detials.caseId')}
          </Typography>
        </Grid>
        <Grid item xs={12} md={10}>
          <Typography variant="body2" color="base.grey3" mb={2}>
            {caseId}
          </Typography>
        </Grid>
      </Grid>
      <EconsultExportEmrDialog
        isOpen={isEmrDialogOpen}
        onClose={() => setIsEmrDialogOpen(false)}
        onConfirm={exportToEmr}
      />
    </>
  );
};

export default EconsultCaseHeader;
